import { METRICTYPE } from 'src/utils/entityDefinitions';

export const SelectedAdMetricQueryParams = 'SelectedAdMetrics';

export enum AdvertisingMetric {
  CPM = 'costPerMilli',

  AD_UNITS_SOLD = 'unitsSold',

  // Reach
  AD_IMPRESSION = 'impressions',
  AD_CLICK = 'clicks',
  CTR = 'clickThroughRate',
  TARGETS = 'targetingText',
  CAMPAIGN_TYPE = 'campaignType',

  // Ad Spend
  AD_SPEND = 'spend',
  CPC = 'costPerClick',
  CPA = 'costPerAcquisition',
  ACOS = 'advertisingCostOfSale',

  // Ad Sales
  AD_SALES = 'sales',
  ROAS = 'returnOnAdSpend',
  CONVERSION = 'conversionRate',
  ORDERS = 'conversions',

  // Incrementality
  INCREMENTAL_SALES = 'brandTotalClicksIncrementalSales',
  INCREMENTAL_ROAS = 'brandTotalClicksIncrementalROAS',
  INCREMENTAL_CLICKS = 'brandTotalClicksIncrementalClicks',
  INCREMENTALITY = 'avgIncrementality',
  INCREMENTAL_UNITS = 'brandTotalClicksIncrementalUnitsOrders'
}

export const AdvertisingMetricLabels = {
  AD_IMPRESSION: 'Ad Impressions',
  AD_CLICK: 'Ad Clicks',
  CTR: 'CTR %',
  TARGETS: 'Targets',
  AD_SPEND: 'Ad Spend',
  CPC: 'CPC',
  CPA: 'CPA',
  ACOS: 'ACOS',
  AD_SALES: 'Ad Sales',
  ROAS: 'ROAS',
  CONVERSION: 'Conversion',
  ORDERS: 'Orders',
  INCREMENTAL_SALES: 'Incremental Sales',
  INCREMENTAL_ROAS: 'Incremental ROAS',
  INCREMENTAL_CLICKS: 'Incremental Clicks',
  INCREMENTALITY: 'Incrementality',
  INCREMENTAL_UNITS: 'Incremental Units',
  CPM: 'CPM',
  CAMPAIGN_TYPE: 'Campaign Type',
  AD_UNITS_SOLD: 'Units Sold',
  KEYWORDS: 'Keywords'
};

/**
 * Metric IDs for which "lower is better" (inverted change colors: green = decrease, red = increase).
 * Used by MetricSummaryTrendCardV2 (via series id) and AdvertisingSummaryMetricsCards.
 */
export const AD_SPEND_METRIC_IDS = [
  AdvertisingMetric.AD_SPEND,
  AdvertisingMetric.CPC,
  AdvertisingMetric.CPA,
  AdvertisingMetric.ACOS,
  AdvertisingMetric.CPM
] as const;

/** Series id suffixes for ad-spend metrics (primarySeries.id includes one of these). */
export const AD_SPEND_SERIES_IDS = AD_SPEND_METRIC_IDS.map((id) => `advertising-${id}`);

export const isAdSpendMetricId = (id: string): boolean =>
  (AD_SPEND_METRIC_IDS as readonly string[]).includes(id);

/**
 *  Key Metric Cards' first column => Reach
 */
export const keyMetricsReach = [
  {
    label: AdvertisingMetricLabels.AD_IMPRESSION,
    id: AdvertisingMetric.AD_IMPRESSION,
    metricType: METRICTYPE.VOLUME
  },

  {
    label: AdvertisingMetricLabels.AD_CLICK,
    id: AdvertisingMetric.AD_CLICK,
    metricType: METRICTYPE.VOLUME
  },

  {
    label: AdvertisingMetricLabels.CTR,
    id: AdvertisingMetric.CTR,
    metricType: METRICTYPE.PERCENT
  },

  {
    label: AdvertisingMetricLabels.TARGETS,
    id: AdvertisingMetric.TARGETS,
    metricType: METRICTYPE.VOLUME
  }
];

/**
 * Key Metric Cards' second column => Ad Spend
 */
export const keyMetricsAdSpend = [
  {
    label: AdvertisingMetricLabels.AD_SPEND,
    id: AdvertisingMetric.AD_SPEND,
    metricType: METRICTYPE.MONEY
  },
  { label: AdvertisingMetricLabels.CPC, id: AdvertisingMetric.CPC, metricType: METRICTYPE.MONEY },
  {
    label: AdvertisingMetricLabels.CPA,
    id: AdvertisingMetric.CPA,
    metricType: METRICTYPE.MONEY
  },
  {
    label: AdvertisingMetricLabels.ACOS,
    id: AdvertisingMetric.ACOS,
    metricType: METRICTYPE.PERCENT
  }
];

/**
 * Key Metric Cards' third column => Ad Sales
 */
export const keyMetricsAdSales = [
  { label: AdvertisingMetricLabels.AD_SALES, id: AdvertisingMetric.AD_SALES, metricType: METRICTYPE.MONEY },
  { label: AdvertisingMetricLabels.ROAS, id: AdvertisingMetric.ROAS, metricType: METRICTYPE.MONEY },
  { label: AdvertisingMetricLabels.CONVERSION, id: AdvertisingMetric.CONVERSION, metricType: METRICTYPE.PERCENT },
  { label: AdvertisingMetricLabels.ORDERS, id: AdvertisingMetric.ORDERS, metricType: METRICTYPE.VOLUME }
];

/**
 * Key Metric Cards' fourth column => Incrementality
 */
export const keyMetricsIncrementality = [
  { label: AdvertisingMetricLabels.INCREMENTAL_SALES, id: AdvertisingMetric.INCREMENTAL_SALES, metricType: METRICTYPE.MONEY },
  { label: AdvertisingMetricLabels.INCREMENTAL_ROAS, id: AdvertisingMetric.INCREMENTAL_ROAS, metricType: METRICTYPE.MONEY },
  { label: AdvertisingMetricLabels.INCREMENTAL_CLICKS, id: AdvertisingMetric.INCREMENTAL_CLICKS, metricType: METRICTYPE.VOLUME },
  { label: AdvertisingMetricLabels.INCREMENTALITY, id: AdvertisingMetric.INCREMENTALITY, metricType: METRICTYPE.PERCENT }
];
